/**
 * „Hol vagyok?” — a legjobb ismert hely egy egyszeri kérdéshez (#86).
 *
 * WEBEN a telefon utolsó, a fiókba megosztott helye nyer (`private/position`,
 * `source: 'device'`): az asztali böngésző WiFi-becslése kilométereket téved.
 * Mérve (Geri, 2026-10-09): a fiókban a telefon 4 perces, 4 m-es fixe állt
 * (Gazdagrét, Tűzkő utca), az asztali asszisztens mégis a böngésző becslését
 * (Bikszádi utca, ~2,5 km) vette — a Home-ról és a tervező „Jelenlegi helyzet”
 * rajtjánál a böngészőt kérdeztük. A térkép már az 1.60.10 óta a telefonét
 * használja (`choosePosition`); ez a kettő maradt ki.
 *
 * A TELEFONON a saját GPS az igazság — ott nincs változás.
 */
import { Capacitor } from '@capacitor/core';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { currentPosition, type CurrentFix, type CurrentPositionOptions } from '@/lib/currentPosition';
import { choosePosition, type PositionFix } from '@/lib/positionQuality';

/** A fiókba megosztott helyzet, ha van és nem böngészőből jött. */
export async function sharedPhonePosition(): Promise<PositionFix | null> {
  const uid = auth?.currentUser?.uid;
  if (!db || !uid) return null;
  try {
    const snapshot = await getDoc(doc(db, 'users', uid, 'private', 'position'));
    const value = snapshot.data() as Record<string, unknown> | undefined;
    const lat = Number(value?.lat);
    const lng = Number(value?.lng);
    if (!value || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const source = value.source === 'browser' || value.source === 'device' ? value.source : undefined;
    const fix: PositionFix = { lat, lng, accuracyM: Number(value.accuracyM ?? 99_999), at: Number(value.at ?? 0), source };
    /* Ugyanaz a döntés, mint a térképnél: weben a nem-böngészős megosztott hely nyer. */
    return choosePosition(null, fix, false, Date.now());
  } catch {
    return null;
  }
}

/**
 * A legjobb ismert hely: weben előbb a telefoné, aztán a böngészőé; a
 * telefonon a saját mérés. A hibát a `currentPosition`-től örökli (a hívó
 * ugyanúgy kezeli, mint eddig).
 */
export async function knownPosition(options: CurrentPositionOptions = {}): Promise<Pick<CurrentFix, 'lat' | 'lng'>> {
  if (!Capacitor.isNativePlatform()) {
    const shared = await sharedPhonePosition();
    if (shared && shared.source !== 'browser') return { lat: shared.lat, lng: shared.lng };
  }
  const fix = await currentPosition(options);
  return { lat: fix.lat, lng: fix.lng };
}
